import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import axios from "axios";

import useSnackbarAlert from "../../utils/snackbarHandlers";

// how long a status check stays good for, so navigating around the app does not
// fire a request on every single route change
const CHECK_TTL_MS = 60 * 1000;
// a session left sitting on one screen is re-checked on this interval
const RECHECK_INTERVAL_MS = 5 * 60 * 1000;

let lastCheckedAt = 0;

/**
 * Deleting a shop only deactivates it, and the login check alone would let an
 * already signed-in shop keep working until it next logs out. This re-checks the
 * stored account against the server and signs it out once it is no longer active.
 *
 * The check never blocks rendering, and a failed request (offline, server down)
 * leaves the session alone.
 */
const AccountStatusGuard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const { showErrorMessage } = useSnackbarAlert();

  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";

  React.useEffect(() => {
    let cancelled = false;

    const signOutDeactivatedAccount = (force: boolean) => {
      const storedUser = localStorage.getItem("UserData");
      if (!storedUser) return;

      let uId: any;
      try {
        uId = JSON.parse(storedUser)?.uId;
      } catch (error) {
        // unreadable session data is not something we can verify
        return;
      }
      if (!uId) return;

      if (!force && Date.now() - lastCheckedAt < CHECK_TTL_MS) return;
      lastCheckedAt = Date.now();

      axios
        .get(`${apiBaseURL}/api/users/accountStatus?id=${uId}`)
        .then((response: any) => {
          if (cancelled) return;
          if (response?.data?.isActive === false) {
            localStorage.clear();
            showErrorMessage(t("accountDeactivated"));
            navigate("/login");
          }
        })
        .catch((error: any) => {
          // a failed check must not sign anyone out
          lastCheckedAt = 0;
          console.log("Account status check failed", error);
        });
    };

    signOutDeactivatedAccount(false);
    const timer = setInterval(() => signOutDeactivatedAccount(true), RECHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [location.pathname]);

  return null;
};

export default AccountStatusGuard;
